import { Button, Form, Input, Spin, message } from "antd";
import {
    AUTH_EMTICATION_STATE,
    AUTH_EMTICATION_STATE_MAP,
    AUTH_PERSON_STATE_MAP,
    CERTIFY_TYPE,
    CUSTOMER_PHONE_STATE
} from "@zhihaoscm/meta";
import { useEffect, useState, type ReactNode } from "react";
import { certificationPhoneService } from "@/api/certificationPhoneService";
import { Breadcrumb } from "@/components/common";
import { AuthFields, CertificationSteps } from "./AuthenticationPage";

interface PersonalForm {
    name: string;
    idNo: string;
}
interface PhoneAuthDetail {
    personalCertification?: { status?: number; name?: string; idNo?: string };
    enterpriseCertification?: { status?: number; unifiedSocialCreditCode?: string };
    customerChangeMobile?: { currentState?: number };
    institutionName?: string;
    institutionApply?: { state?: number };
}

/** 换绑手机号认证页，按个人、系统企业、契约锁企业和企业授权四级状态推进。 */
export function PhoneAuthenticationPage() {
    const [form] = Form.useForm<PersonalForm>();
    const [type, setType] = useState<number>(CERTIFY_TYPE.PERSONAL.code);
    const [personState, setPersonState] = useState<number>(AUTH_PERSON_STATE_MAP.PENDING);
    const [enterpriseState, setEnterpriseState] = useState<number>(AUTH_EMTICATION_STATE_MAP.NOT_STATE);
    const [contractState, setContractState] = useState<number>(AUTH_EMTICATION_STATE.notState);
    const [phoneState, setPhoneState] = useState<number>(CUSTOMER_PHONE_STATE.UNREGISTERED.code);
    const [authorized, setAuthorized] = useState(false);
    const [institutionName, setInstitutionName] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    /** 加载换绑专用认证详情并恢复用户当前所处步骤。 */
    const load = async () => {
        setLoading(true);
        try {
            const detail = (await certificationPhoneService.getUserAuthDetail()) as PhoneAuthDetail;
            const personal = detail?.personalCertification;
            const nextPersonState = personal?.status ?? AUTH_PERSON_STATE_MAP.PENDING;
            const nextPhoneState = detail?.customerChangeMobile?.currentState ?? CUSTOMER_PHONE_STATE.UNREGISTERED.code;
            setPersonState(nextPersonState);
            setContractState(detail?.enterpriseCertification?.status ?? AUTH_EMTICATION_STATE.notState);
            setPhoneState(nextPhoneState);
            setAuthorized(
                Boolean(detail?.enterpriseCertification?.unifiedSocialCreditCode) &&
                    nextPhoneState === CUSTOMER_PHONE_STATE.CORPORATEAUTH.code
            );
            setInstitutionName(detail?.institutionName ?? "");
            form.setFieldsValue({ name: personal?.name ?? "", idNo: personal?.idNo ?? "" });
            if (nextPersonState === AUTH_PERSON_STATE_MAP.SUCCESS) {
                setType(CERTIFY_TYPE.ENTERPRISE.code);
                setEnterpriseState(
                    detail?.institutionName
                        ? AUTH_EMTICATION_STATE_MAP.SUCCESS
                        : (detail?.institutionApply?.state ?? AUTH_EMTICATION_STATE_MAP.NOT_STATE)
                );
            } else setType(CERTIFY_TYPE.PERSONAL.code);
        } catch {
            void message.error("换绑认证信息加载失败，请稍后重试");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, []);

    /** 契约锁个人认证在外部页面完成，回调到个人信息页继续换绑。 */
    const submitPersonal = async () => {
        const values = await form.validateFields();
        setSubmitting(true);
        try {
            const callbackPage = window.location.href.replace("authentication/phone", "info");
            const data = await certificationPhoneService.getPersonalAuth({ ...values, callbackPage } as any);
            if (data?.certificationLink) window.location.assign(data.certificationLink);
        } catch {
            void message.error("个人认证发起失败，请稍后重试");
        } finally {
            setSubmitting(false);
        }
    };

    /** 根据契约锁状态选择企业认证、查看认证进度或企业授权链接。 */
    const continueEnterprise = async () => {
        setSubmitting(true);
        try {
            if (
                contractState === AUTH_EMTICATION_STATE.success &&
                phoneState === CUSTOMER_PHONE_STATE.ENTERPRISEAUTH.code
            ) {
                const authorizeUrl = await certificationPhoneService.getEnterpriseAuthorize(institutionName);
                if (authorizeUrl) window.open(authorizeUrl);
                return;
            }
            if (
                contractState === AUTH_EMTICATION_STATE.notState &&
                phoneState === CUSTOMER_PHONE_STATE.SYSENTERPRISEAUTH.code
            )
                await certificationPhoneService.submitEnterpriseAuthorize(institutionName);
            const data = await certificationPhoneService.getEnterpriseAuth();
            if (data?.certificationLink) window.open(data.certificationLink);
        } catch {
            void message.error("组织机构认证发起失败，请稍后重试");
        } finally {
            setSubmitting(false);
        }
    };

    const personal = form.getFieldsValue();
    if (loading) return <Spin fullscreen tip="换绑认证信息加载中" />;
    return (
        <section className="phone-authentication-page">
            <div className="section-card phone-authentication-card">
                <Breadcrumb
                    routes={[{ name: "账号管理" }, { name: "认证", path: "/me/authentication" }, { name: "认证" }]}
                />
                <div className="phone-certify-page">
                    <div className="authentication-title">认证</div>
                    <CertificationSteps
                        type={type}
                        personState={personState}
                        enterpriseState={authorized ? AUTH_EMTICATION_STATE_MAP.SUCCESS : enterpriseState}
                        completeIcon="icon-a-Group1312315248"
                        completeOnlyWhenInactive
                        onChange={value => {
                            if (value === CERTIFY_TYPE.PERSONAL.code || personState === AUTH_PERSON_STATE_MAP.SUCCESS)
                                setType(value);
                        }}
                    />
                    <div className="phone-auth-line" />
                    {type === CERTIFY_TYPE.PERSONAL.code ? (
                        <section className="phone-auth-panel">
                            {personState === AUTH_PERSON_STATE_MAP.PENDING ? (
                                <Form form={form} className="phone-auth-form">
                                    <Form.Item
                                        name="name"
                                        label="真实姓名"
                                        rules={[{ required: true, message: "请输入您的真实姓名" }]}
                                    >
                                        <Input maxLength={25} />
                                    </Form.Item>
                                    <Form.Item
                                        name="idNo"
                                        label="身份证号"
                                        rules={[
                                            { required: true, message: "请输入您的身份证号码" },
                                            {
                                                pattern: /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/,
                                                message: "请输入长度15或18个字符的正确的身份证号"
                                            }
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                    <Button type="primary" loading={submitting} onClick={() => void submitPersonal()}>
                                        下一步
                                    </Button>
                                </Form>
                            ) : (
                                <PhoneAuthStatus
                                    fields={[
                                        { label: "真实姓名", value: personal.name || "--" },
                                        { label: "身份证号", value: personal.idNo || "--" }
                                    ]}
                                    image={
                                        personState === AUTH_PERSON_STATE_MAP.SUCCESS
                                            ? "auth-success.png"
                                            : "auth-failed.png"
                                    }
                                >
                                    {personState === AUTH_PERSON_STATE_MAP.SUCCESS ? (
                                        <>
                                            <span>您的认证申请已通过</span>
                                            {enterpriseState === AUTH_EMTICATION_STATE_MAP.NOT_STATE && !authorized ? (
                                                <>
                                                    <span>，</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setType(CERTIFY_TYPE.ENTERPRISE.code)}
                                                    >
                                                        前往组织机构认证
                                                        <i className="iconfont icon-left" />
                                                    </button>
                                                </>
                                            ) : null}
                                        </>
                                    ) : (
                                        <>
                                            <span>您的认证申请已经被驳回</span>
                                            <button
                                                type="button"
                                                onClick={() => setPersonState(AUTH_PERSON_STATE_MAP.PENDING)}
                                            >
                                                重新申请个人实名认证
                                                <i className="iconfont icon-left" />
                                            </button>
                                        </>
                                    )}
                                </PhoneAuthStatus>
                            )}
                        </section>
                    ) : (
                        <section className="phone-auth-panel">
                            {enterpriseState !== AUTH_EMTICATION_STATE_MAP.NOT_STATE ? (
                                <PhoneAuthStatus
                                    fields={[{ label: "企业名称", value: institutionName || "--" }]}
                                    image={
                                        authorized || enterpriseState === AUTH_EMTICATION_STATE_MAP.SUCCESS
                                            ? "auth-success.png"
                                            : enterpriseState === AUTH_EMTICATION_STATE_MAP.PENDING
                                              ? "auth-pending.png"
                                              : "auth-failed.png"
                                    }
                                >
                                    {authorized ? (
                                        <span>您已通过组织机构认证且已授权</span>
                                    ) : enterpriseState === AUTH_EMTICATION_STATE_MAP.PENDING ? (
                                        <span>认证申请等待审核中</span>
                                    ) : enterpriseState === AUTH_EMTICATION_STATE_MAP.ERROR ? (
                                        <>
                                            <span>您的认证申请已被驳回，</span>
                                            <button
                                                type="button"
                                                onClick={() => setEnterpriseState(AUTH_EMTICATION_STATE_MAP.NOT_STATE)}
                                            >
                                                重新申请组织机构认证
                                                <i className="iconfont icon-left" />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <span>您的认证申请已通过，</span>
                                            <button
                                                type="button"
                                                disabled={!institutionName || submitting}
                                                onClick={() => void continueEnterprise()}
                                            >
                                                还需授权电子签
                                                <i className="iconfont icon-left" />
                                            </button>
                                        </>
                                    )}
                                </PhoneAuthStatus>
                            ) : null}
                        </section>
                    )}
                </div>
            </div>
            {type !== CERTIFY_TYPE.PERSONAL.code &&
            enterpriseState === AUTH_EMTICATION_STATE_MAP.SUCCESS &&
            !authorized ? (
                <PhoneAuthNotice
                    lines={[
                        "1.您已完成组织机构认证，可作为供应商发布商品。",
                        "2.若要作为供应商、联合购销商进行项目交易，还需完成电子签章授权。"
                    ]}
                />
            ) : null}
            {type !== CERTIFY_TYPE.PERSONAL.code && authorized ? (
                <PhoneAuthNotice lines={["您已完成组织机构认证且已授权，可作为供应商、联合购销商进行项目交易。"]} />
            ) : null}
        </section>
    );
}

function PhoneAuthStatus({
    fields,
    image,
    children
}: {
    fields: Array<{ label: string; value: string }>;
    image: string;
    children: ReactNode;
}) {
    return (
        <div className="phone-auth-content">
            <AuthFields fields={fields} />
            <div className="phone-auth-status">
                <img src={`https://static.zhihaoscm.cn/customer-admin/${image}`} alt="认证状态" />
                <div>{children}</div>
            </div>
            <div />
        </div>
    );
}
function PhoneAuthNotice({ lines }: { lines: string[] }) {
    return (
        <div className="phone-auth-notice">
            <div className="phone-auth-divider">
                <i />
                <span>提示</span>
                <i />
            </div>
            {lines.map(line => (
                <div key={line}>{line}</div>
            ))}
        </div>
    );
}
